/* eslint-disable react/jsx-props-no-spreading */
// @react-functional
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useModal } from './useModal';
import { ModalType } from './ModalContext';

/**
 * This hook allow us to manipulate modal based on query params.
 * @param type string The name of the modal, will be used for the query params, ?modal=type.
 * @param Component ModalType<CustomProps> The modal component to trigger.
 */
export const useSearchParamsModal = <CustomProps,>(
  type: string,
  Component: ModalType<CustomProps>,
) => {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const modalParam = searchParams.get('modal');

  const openModal = () => {
    // On modal open, we add the modal query params to the url
    searchParams.append('modal', type);
    history.push({
      search: searchParams.toString(),
    });
  };

  const {
    openModal: openModalDefault,
    closeModal: closeModalDefault,
  } = useModal(
    (modalProps, customProps) => (
      <Component
        {...customProps}
        {...modalProps}
        onRequestClose={(query: string) => {
          // Persist query if provided.
          // Persist version query param on modal close.
          const versionParam = searchParams.get('version');
          const persistedQuery = typeof query === 'string' ? query : '';
          history.push({
            search: `${
              versionParam ? `version=${versionParam}&` : ''
            }${persistedQuery}`,
          });
          closeModalDefault();
        }}
      />
    ),
    [search],
  );

  useEffect(() => {
    // This allow us to open the modal at load time if the query params modal is here
    if (modalParam === type) openModalDefault();
    // In the same way, if there is no query params modal, we close the modals
    if (!modalParam) closeModalDefault();
  }, [modalParam]);

  return { openModal };
};
